import { useCallback, useEffect, useState } from 'react';

import { useApolloClient } from '@apollo/client';

import { storageApiRef, useApi } from '@backstage/core-plugin-api';

import {
  type MutationSignal,
  type MutationSignalHandler,
  readDisplayNameFromCache,
} from '@spotify/confidence-graphql';

import { getPathMatchLength } from '../../utils/path-match';
import { useAuth } from '../auth-factory';
import {
  RECENT_ACTIVITY_TYPENAMES,
  getResourceRoute,
  getResourceTypename,
} from './recentActivityConfig';
import { useStorageKey } from './useStorageKey';

export type RecentActivityItem = {
  resourceName: string;
  displayName?: string;
  typename?: string;
  route: string;
  updatedAt: number;
};

const MAX_ITEMS = 20;

const resourceNameToTypename = (resourceName: string): string | undefined => {
  const collection = resourceName.split('/')[0];
  if (collection === 'workflows') return RECENT_ACTIVITY_TYPENAMES.workflows;
  return getResourceTypename(collection);
};

const normalizeResourceName = (resourceName: string): string => {
  const parts = resourceName.split('/');

  if (
    parts[0] === 'workflows' &&
    parts.length >= 4 &&
    parts[2] === 'instances'
  ) {
    return parts.slice(0, 4).join('/');
  }

  if (parts.length > 2) {
    return parts.slice(0, 2).join('/');
  }

  return resourceName;
};

const resourceNameToRoute = (resourceName: string): string | undefined => {
  const parts = resourceName.split('/');

  if (
    parts[0] === 'workflows' &&
    parts.length >= 4 &&
    parts[2] === 'instances'
  ) {
    return `/${parts.slice(0, 4).join('/')}`;
  }

  const routeBase = getResourceRoute(parts[0]);
  if (routeBase && parts[1]) {
    return `${routeBase}/${parts[1]}`;
  }

  return undefined;
};

const parseItems = (value: unknown): RecentActivityItem[] =>
  Array.isArray(value) ? value : [];

const findViewedItemIndex = (
  items: RecentActivityItem[],
  pathname: string,
): number => {
  let bestIndex = -1;
  let bestLength = 0;
  items.forEach((item, index) => {
    const length = getPathMatchLength(pathname, item.route);
    if (length > bestLength) {
      bestLength = length;
      bestIndex = index;
    }
  });
  return bestIndex;
};

export const useRecentActivity = () => {
  const storage = useApi(storageApiRef);
  const client = useApolloClient();
  const { orgName: workspaceId } = useAuth();
  const userStorageKey = useStorageKey('recentActivity');
  const key = workspaceId ? `${userStorageKey}-${workspaceId}` : undefined;

  const [items, setItems] = useState<RecentActivityItem[]>(() =>
    key ? parseItems(storage.snapshot<RecentActivityItem[]>(key).value) : [],
  );

  useEffect(() => {
    if (!key) {
      setItems([]);
      return undefined;
    }

    setItems(parseItems(storage.snapshot<RecentActivityItem[]>(key).value));

    const subscription = storage
      .observe$<RecentActivityItem[]>(key)
      .subscribe(snapshot => {
        setItems(parseItems(snapshot.value));
      });

    return () => subscription.unsubscribe();
  }, [key, storage]);

  const recordActivity: MutationSignalHandler = useCallback(
    (event: MutationSignal) => {
      if (!key) return;

      const resourceName = normalizeResourceName(event.resourceName);
      const wasNormalized = resourceName !== event.resourceName;

      const current = parseItems(
        storage.snapshot<RecentActivityItem[]>(key).value,
      );

      if (event.kind === 'delete' && !wasNormalized) {
        const filtered = current.filter(
          item => item.resourceName !== resourceName,
        );
        if (filtered.length !== current.length) {
          storage.set(key, filtered);
        }
        return;
      }

      const route = resourceNameToRoute(resourceName);
      if (!route) return;

      const existing = current.find(item => item.resourceName === resourceName);
      const typename =
        existing?.typename ?? resourceNameToTypename(resourceName);
      const signalDisplayName = wasNormalized ? undefined : event.displayName;
      const displayName =
        signalDisplayName ??
        existing?.displayName ??
        readDisplayNameFromCache(client, resourceName, typename);
      const newItem: RecentActivityItem = {
        resourceName,
        displayName,
        typename,
        route,
        updatedAt: Date.now(),
      };

      const withoutDuplicate = current.filter(
        item => item.resourceName !== resourceName,
      );
      storage.set(key, [newItem, ...withoutDuplicate].slice(0, MAX_ITEMS));
    },
    [key, storage, client],
  );

  const recordView = useCallback(
    (pathname: string) => {
      if (!key) return;
      const current = parseItems(
        storage.snapshot<RecentActivityItem[]>(key).value,
      );
      const viewedIndex = findViewedItemIndex(current, pathname);
      if (viewedIndex <= 0) return;

      const viewedItem: RecentActivityItem = {
        ...current[viewedIndex],
        updatedAt: Date.now(),
      };
      storage.set(key, [
        viewedItem,
        ...current.filter((_, index) => index !== viewedIndex),
      ]);
    },
    [key, storage],
  );

  const dismissItem = useCallback(
    (resourceName: string) => {
      if (!key) return;
      const current = parseItems(
        storage.snapshot<RecentActivityItem[]>(key).value,
      );
      storage.set(
        key,
        current.filter(item => item.resourceName !== resourceName),
      );
    },
    [key, storage],
  );

  return {
    recentActivityItems: items,
    recordActivity,
    recordView,
    dismissItem,
  };
};
